import React from 'react';

import CircularProgress from '@mui/material/CircularProgress';

import {accessItemTypeEnum} from '../../../serverClientShared/sharedEnums';
import {useContentTreeContext} from './ContentTreeContext';
import ContentTreeNode from './ContentTreeNode';

const buildChildNestedRoute = (child, parentNestedRoute) => {
    if (child.type === accessItemTypeEnum.FOLDER) return `${parentNestedRoute}/${child.id}`;
    return parentNestedRoute;
};

const LoadMoreButton = ({onClick, isLoading}) => (
    <div
        className="flex items-center justify-center py-2 cursor-pointer text-xs text-link-normal hover:underline"
        onClick={isLoading ? undefined : onClick}
    >
        {isLoading ? <CircularProgress size={14} /> : 'Load more...'}
    </div>
);

const ChildrenList = ({parentNestedRoute, nodeId, depth}) => {
    const {
        childrenCache, visibleCounts, expandedNodes, loadingNodes,
        hasMoreChildren, handleExpand, handleLoadMoreChildren,
        renderNodePrefix, renderNodeSuffix, getNodeClassName, getNodeTooltip, getNodeIcon,
    } = useContentTreeContext();

    const cached = childrenCache[nodeId];
    if (!cached) return null;

    const allChildren = [...(cached.folders || []), ...(cached.items || [])];
    const visibleLimit = visibleCounts?.[nodeId] || 20;
    const visibleChildren = visibleLimit ? allChildren.slice(0, visibleLimit) : allChildren;

    return (
        <>
            {visibleChildren.map((child) => {
                const childType = child.type || accessItemTypeEnum.ITEM;
                const childNestedRoute = buildChildNestedRoute(child, parentNestedRoute);
                const isChildExpanded = expandedNodes.has(child.id);
                const isChildLoading = loadingNodes.has(child.id);

                return (
                    <React.Fragment key={child.id}>
                        <ContentTreeNode
                            node={child}
                            nodeType={childType}
                            depth={depth + 1}
                            isExpanded={isChildExpanded}
                            isLoading={isChildLoading}
                            renderPrefix={renderNodePrefix ? () => renderNodePrefix(child, childType) : undefined}
                            renderSuffix={renderNodeSuffix ? () => renderNodeSuffix(child, childType, childNestedRoute) : undefined}
                            onExpand={() => handleExpand(child.id, childType, childNestedRoute)}
                            nameClassName={getNodeClassName ? getNodeClassName(child, childType) : undefined}
                            tooltipText={getNodeTooltip ? getNodeTooltip(child, childType) : undefined}
                            iconOverride={getNodeIcon ? getNodeIcon(child, childType) : undefined}
                        />
                        {isChildExpanded && childType === accessItemTypeEnum.FOLDER && (
                            <ChildrenList
                                parentNestedRoute={childNestedRoute}
                                nodeId={child.id}
                                depth={depth + 1}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {hasMoreChildren(nodeId) && (
                <div style={{paddingLeft: (depth + 1) * 24 + 8}}>
                    <LoadMoreButton
                        onClick={() => handleLoadMoreChildren(nodeId)}
                        isLoading={false}
                    />
                </div>
            )}
        </>
    );
};

export default ChildrenList;
